import { Injectable } from '@angular/core';
import { VaultApiService } from '@services/api/vault-api.service';
import {
  CreditScoreSegmentUpdateRequest,
  CreditScoreSpreadsQuery,
} from '@interfaces/credit-score/requests/credit-score.request';
import {
  CreditScoreApplySuggestionsDto,
  CreditScoreCompanyDto,
  CreditScoreMethodologyDto,
  CreditScoreMethodologyVersionSummaryDto,
  CreditScorePortfolioDto,
  CreditScoreSegmentUpdateDto,
  CreditScoreSpreadsDto,
} from '@interfaces/credit-score/responses/credit-score.response';
import { Observable } from 'rxjs';

/**
 * HTTP do score de crédito (card 3766), sobre o `VaultApiService` (`api/v1/credit-score`).
 * Usado pela Análise Score (Performance) e pelo bloco do score no topo da Análise Econômica.
 * Não faz conta: o score, a cobertura, as contribuições e o histórico vêm prontos do backend.
 */
@Injectable({ providedIn: 'root' })
export class CreditScoreHttpService {
  private readonly base = 'credit-score';

  constructor(private readonly api: VaultApiService) {}

  /** Carteira do tenant. `refresh=true` pede a remontagem (a resposta pode vir com `refreshing=true`). */
  getPortfolio(refresh = false): Observable<CreditScorePortfolioDto | null> {
    return this.api.get<CreditScorePortfolioDto>(`${this.base}/portfolio`, null, { refresh });
  }

  /**
   * Score de uma empresa (exige DFP_VIEW e escopo da empresa). Com `refresh`, a API relê os dados financeiros da
   * empresa e grava (Recarregar esta empresa, card 3842).
   */
  getCompany(companyId: string, refresh = false): Observable<CreditScoreCompanyDto | null> {
    return this.api.get<CreditScoreCompanyDto>(
      `${this.base}/companies/${encodeURIComponent(companyId)}`,
      null,
      refresh ? { refresh } : undefined
    );
  }

  /** Spread de crédito CMA por rating num prazo (meses). Sem `date`, o último fechamento. */
  getSpreads(query: CreditScoreSpreadsQuery): Observable<CreditScoreSpreadsDto | null> {
    const params: Record<string, unknown> = { months: query.months };
    if (query.date) params['date'] = query.date;
    return this.api.get<CreditScoreSpreadsDto>(`${this.base}/spreads`, null, params);
  }

  getMethodology(): Observable<CreditScoreMethodologyDto | null> {
    return this.api.get<CreditScoreMethodologyDto>(`${this.base}/methodology`);
  }

  /** Versões da metodologia, da mais recente para a mais antiga. */
  getMethodologyVersions(): Observable<CreditScoreMethodologyVersionSummaryDto[] | null> {
    return this.api.get<CreditScoreMethodologyVersionSummaryDto[]>(
      `${this.base}/methodology/versions`
    );
  }

  /** Grava uma nova versão ativa (version/updatedAt/updatedBy são ignorados pelo backend). */
  saveMethodology(
    methodology: CreditScoreMethodologyDto
  ): Observable<CreditScoreMethodologyDto | null> {
    return this.api.put<CreditScoreMethodologyDto>(`${this.base}/methodology`, methodology);
  }

  updateSegment(
    companyId: string,
    segmentId: string
  ): Observable<CreditScoreSegmentUpdateDto | null> {
    const body: CreditScoreSegmentUpdateRequest = { segmentId };
    return this.api.put<CreditScoreSegmentUpdateDto>(
      `${this.base}/companies/${encodeURIComponent(companyId)}/segment`,
      body
    );
  }

  /** Grava o segmento sugerido de toda empresa que ainda não tem segmento salvo. */
  applySegmentSuggestions(): Observable<CreditScoreApplySuggestionsDto | null> {
    return this.api.post<CreditScoreApplySuggestionsDto>(
      `${this.base}/segments/apply-suggestions`,
      {}
    );
  }
}
